import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, PencilSimple, Power, GasPump, PaperPlaneTilt } from '@phosphor-icons/react';
import SuperAdminDeleteButton, { isSuperAdmin } from '../../components/SuperAdminDeleteButton';
import { useAuth } from '../../contexts/AuthContext';

export default function FuelSuppliers() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', mobile: '', address: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [reportRunning, setReportRunning] = useState(false);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/fuel-suppliers');
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchRows(); }, []);

  const reset = () => { setForm({ name: '', mobile: '', address: '' }); setEditing(null); setError(''); };
  const openCreate = () => { reset(); setDialogOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ name: r.name, mobile: r.mobile, address: r.address || '' }); setError(''); setDialogOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      if (editing) {
        await api.put(`/admin/fuel-suppliers/${editing.id}`, form);
      } else {
        await api.post('/admin/fuel-suppliers', form);
      }
      setDialogOpen(false); reset(); await fetchRows();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const handleToggle = async (r) => {
    setBusy(true);
    try {
      const newStatus = r.status === 'active' ? 'inactive' : 'active';
      await api.put(`/admin/fuel-suppliers/${r.id}`, { status: newStatus });
      fetchRows();
    } catch {} finally { setBusy(false); }
  };

  const runReportNow = async () => {
    if (!window.confirm('Generate the weekly fuel report PDF for the previous Thursday → Wednesday window and WhatsApp it to every active supplier RIGHT NOW?')) return;
    setReportRunning(true);
    try {
      await api.post('/admin/fuel-report/run-now');
      alert('Weekly fuel report sent. Check WhatsApp logs for delivery status.');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to run fuel report');
    } finally {
      setReportRunning(false);
    }
  };

  return (
    <div data-testid="fuel-suppliers-page" className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl font-black tracking-tighter text-[#0A0A0A]">Fuel Suppliers</h1>
          <p className="text-sm text-[#666] mt-1">Pumps where pilots fill fuel. Each gets a weekly PDF report on Thursday 1 AM IST via WhatsApp.</p>
        </div>
        <div className="flex items-center gap-2">
          {isSuperAdmin(user) && (
            <Button
              data-testid="run-fuel-report-btn"
              onClick={runReportNow}
              disabled={reportRunning}
              className="bg-[#FF8C00] text-white hover:bg-[#FF8C00]/90 rounded-none"
            >
              <PaperPlaneTilt size={16} className="mr-2" />
              {reportRunning ? 'Sending...' : 'Run Weekly Report Now'}
            </Button>
          )}
          <Button data-testid="add-fuel-supplier-btn" onClick={openCreate} className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 rounded-none">
            <Plus size={16} className="mr-2" /> Add Supplier
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E5]">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F3F3F3]">
              <TableHead className="text-xs uppercase tracking-widest">Name</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Mobile</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Address</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={5} className="text-center text-[#666] py-8">Loading...</TableCell></TableRow>}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-[#666] py-8">No fuel suppliers yet. Click "Add Supplier" to create one.</TableCell></TableRow>}
            {!loading && rows.map((r, i) => (
              <TableRow key={r.id} className={i % 2 ? 'bg-[#F3F3F3]/30' : ''} data-testid={`fuel-supplier-row-${r.id}`}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <GasPump size={14} className="text-[#FF8C00] shrink-0" weight="bold" />
                    <span className="font-medium">{r.name}</span>
                  </div>
                </TableCell>
                <TableCell><a href={`tel:${r.mobile}`} className="text-[#002FA7]">{r.mobile}</a></TableCell>
                <TableCell className="max-w-[300px] truncate">{r.address || '-'}</TableCell>
                <TableCell>
                  <span className={`text-xs uppercase tracking-widest px-2 py-1 ${r.status === 'inactive' ? 'bg-[#FF3333]/10 text-[#FF3333]' : 'bg-[#00C853]/10 text-[#00C853]'}`}>
                    {r.status === 'inactive' ? 'Inactive' : 'Active'}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex items-center gap-1">
                    <button onClick={() => openEdit(r)} className="p-2 hover:bg-[#F3F3F3]" data-testid={`edit-fuel-supplier-${r.id}`}>
                      <PencilSimple size={16} />
                    </button>
                    <button
                      onClick={() => handleToggle(r)}
                      disabled={busy}
                      className={`p-2 transition-colors ${r.status === 'active' ? 'hover:bg-[#FF3333]/10 text-[#FF3333]' : 'hover:bg-[#00C853]/10 text-[#00C853]'}`}
                      data-testid={`toggle-fuel-supplier-${r.id}`}
                    >
                      <Power size={16} weight={r.status === 'active' ? 'regular' : 'bold'} />
                    </button>
                    <SuperAdminDeleteButton masterType="fuel-suppliers" recordId={r.id} recordName={r.name} onDeleted={fetchRows} />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) reset(); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Edit Fuel Supplier' : 'Add Fuel Supplier'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-widest">Name *</Label>
              <Input data-testid="fuel-supplier-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required autoFocus className="rounded-none" placeholder="e.g. HP Bunk Coimbatore" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Mobile *</Label>
              <Input data-testid="fuel-supplier-mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} required className="rounded-none" placeholder="10-digit mobile" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Address</Label>
              <Input data-testid="fuel-supplier-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-none" placeholder="Optional" />
            </div>
            {error && <p className="text-xs text-[#FF3333]">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); reset(); }} className="rounded-none">Cancel</Button>
              <Button type="submit" disabled={busy} className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 rounded-none" data-testid="fuel-supplier-save-btn">
                {editing ? 'Save' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
